import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface PaginationProps {
  page: number;
  limit: number;
  total: number;
  onPageChange: (page: number) => void;
}

/**
 * Compact pagination footer. Renders nothing when there's only one page —
 * the table sits flush instead of carrying empty chrome.
 */
export function Pagination({
  page,
  limit,
  total,
  onPageChange,
}: PaginationProps): JSX.Element | null {
  const lastPage = Math.max(1, Math.ceil(total / Math.max(1, limit)));
  if (lastPage <= 1) return null;

  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(total, page * limit);

  return (
    <div className="flex items-center justify-between gap-3 pt-2 text-xs">
      <span className="text-muted-foreground tabular-nums">
        {start.toLocaleString()}–{end.toLocaleString()} of {total.toLocaleString()}
      </span>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          Prev
        </Button>
        <span className="font-mono text-muted-foreground">
          {page} / {lastPage}
        </span>
        <Button
          size="sm"
          variant="outline"
          disabled={page >= lastPage}
          onClick={() => onPageChange(page + 1)}
        >
          Next
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
